import SimpleSelect from "component/Common/simpleSelect";
import TextFormInput from "component/Common/TextFormInput";
import { useState } from "react";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Col,
    Row,
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import COUNTRIES_JSON from "assets/data/countries.json";
import { useHotelContext } from "hooks/hotel/useHotelContext";

const nationalityOptions = COUNTRIES_JSON.map((country) => ({
    value: country.en,
    label: country.en,
    id: Number(country.codePhone),
}));

const nationalityCodeOptions = COUNTRIES_JSON.map((country) => ({
    value: country.en + " ( " + country.codePhone + " ) " || country.en,
    label: country.en + " ( " + country.codePhone + " ) " || country.en,
    id: Number(country.codePhone),
}));

const BookingArea = () => {
    const { control, handleSubmit, getValues } = useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const hotelState = JSON.parse(localStorage.getItem("hotelState") || "{}");
    const rooms = hotelState?.rooms || [];
    const [activeKeys, setActiveKeys] = useState(
        [...Array(rooms.length + 1).keys()].map((i) => i.toString())
    );
    const { setGuestsInformation } = useHotelContext();
    const onSubmit = (data) => {
        setActiveKeys(
            [...Array(rooms.length + 1).keys()].map((i) => i.toString())
        );
        setGuestsInformation(data);
        navigate("/hotel-payment");
    };

    return (
        <>
            <div className="flex flex-col gap-4">
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col w-full p-3 border border-border-color rounded-lg"
                >
                    <h2 className="text-xl font-semibold text-main-color mb-4">
                        Guests Information
                    </h2>
                    <Accordion
                        onSelect={setActiveKeys}
                        alwaysOpen
                        defaultActiveKey="0"
                        className="accordion-icon accordion-bg-light flex flex-col gap-2"
                        id="accordionExample2"
                        activeKey={activeKeys}
                    >
                        <ClientInformation
                            control={control}
                            getValues={getValues}
                        />
                        {rooms?.map((room, index) => (
                            <AccordionItem
                                eventKey={index + 1 + ""}
                                key={index}
                                className="border"
                            >
                                <AccordionHeader className="p-2">
                                    <div className="">
                                        {t("room")} : {index + 1}
                                    </div>
                                </AccordionHeader>
                                <AccordionBody className="mt-3">
                                    <Room
                                        room={room}
                                        roomIdx={index + 1}
                                        control={control}
                                        getValues={getValues}
                                    />
                                </AccordionBody>
                            </AccordionItem>
                        ))}
                    </Accordion>
                    <div className="justify-end mt-4 flex gap-2">
                        <button
                            onClick={() => {
                                navigate("/hotel-details");
                            }}
                            type="button"
                            className="w-[40%] btn border-main-color border-2 hover:text-main-color  bg-transparent text-main-color"
                        >
                            {t("back")}
                        </button>
                        <button
                            type="submit"
                            className="w-[60%] btn bg-main-color text-white"
                        >
                            {t("proceedToPayment")}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default BookingArea;

const ClientInformation = ({ control, getValues }) => {
    const [countries, setCountries] = useState(nationalityOptions);
    const [countriesCode, setCountriesCode] = useState(nationalityCodeOptions);
    const { t, i18n } = useTranslation();

    const handleCountryChange = (inputValue) => {
        const language = i18n.language;
        const tempContry = COUNTRIES_JSON.map((country) => ({
            value: country[language] || country.en,
            label: country[language] || country.en,
            id: Number(country.codePhone),
        }));
        const filteredCountries = tempContry.filter((country) =>
            country.label.toLowerCase().includes(inputValue.toLowerCase())
        );
        setCountries(filteredCountries);
    };

    const handleCountryCodeChange = (inputValue) => {
        const language = i18n.language;
        const tempContryCode = COUNTRIES_JSON.map((country) => ({
            value:
                country[language] + " ( " + country.codePhone + " ) " ||
                country.en,
            label:
                country[language] + " ( " + country.codePhone + " ) " ||
                country.en,
            id: Number(country.codePhone),
        }));
        const filteredCountries = tempContryCode.filter((country) =>
            country.label.toLowerCase().includes(inputValue.toLowerCase())
        );
        setCountriesCode(filteredCountries);
    };

    return (
        <AccordionItem eventKey="0">
            <AccordionHeader className="p-2">
                <div className="">Client Information</div>
            </AccordionHeader>
            <AccordionBody>
                <div className="row">
                    <div className="col-lg-6">
                        <TextFormInput
                            name="email"
                            type="email"
                            label={t("emailAddress")}
                            placeholder={t("enterYourEmailAddress")}
                            containerClass="col-md-6"
                            control={control}
                            rules={{
                                required: t("emailAddressIsRequired"),
                                pattern: {
                                    value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                    message: t("invalidEmailAddress"),
                                },
                            }}
                        />
                    </div>
                    <div className="col-lg-6">
                        <TextFormInput
                            name="verifyCustomerEmail"
                            type="text"
                            label={t("verifyEmail")}
                            placeholder={t("verifyEmail")}
                            containerClass="col-md-6"
                            control={control}
                            rules={{
                                required: t("emailAddressIsRequired"),
                                pattern: {
                                    value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                    message: t("invalidEmailAddress"),
                                },
                                validate: (value) =>
                                    value === getValues("email") ||
                                    "emails do not match",
                            }}
                        />
                    </div>
                    <div className="col-lg-6">
                        <TextFormInput
                            name={"customerMobileNo"}
                            control={control}
                            label={"Mobile No"}
                            rules={{
                                required: t("mobileNumberIsRequired"),
                                pattern: {
                                    value: /^[0-9]{10}$/,
                                    message: t("invalidMobileNumber"),
                                },
                            }}
                            placeholder="Mobile No*"
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="text-sm text-gray-600">
                            {t("countryCode")}{" "}
                            <span className="text-danger">*</span>{" "}
                        </label>
                        <Controller
                            name={`customerCountryCode`}
                            control={control}
                            rules={{
                                required: t("countryCodeIsRequired"),
                            }}
                            defaultValue={nationalityCodeOptions.find(
                                (option) => option.id === 213
                            )}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <SimpleSelect
                                        options={countriesCode}
                                        placeholder={t("selectCountry")}
                                        onChange={(x) => {
                                            field.onChange(x);
                                        }}
                                        onInputChange={handleCountryCodeChange}
                                        clearable={true}
                                        defaultValue={nationalityCodeOptions.find(
                                            (option) => option.id === 213
                                        )}
                                    />
                                    {error && (
                                        <div className="text-red-500 text-[12px]">
                                            {error.message}
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="text-sm text-gray-600">
                            {t("country")}{" "}
                            <span className="text-danger">*</span>{" "}
                        </label>
                        <Controller
                            name={`customerCountry`}
                            control={control}
                            rules={{
                                required: t("countryIsRequired"),
                            }}
                            defaultValue={nationalityOptions.find(
                                (option) => option.id === 213
                            )}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <SimpleSelect
                                        options={countries}
                                        placeholder={t("selectCountry")}
                                        onChange={(x) => {
                                            field.onChange(x);
                                        }}
                                        onInputChange={handleCountryChange}
                                        clearable={true}
                                        defaultValue={nationalityOptions.find(
                                            (option) => option.id === 213
                                        )}
                                    />
                                    {error && (
                                        <div className="text-red-500 text-[12px]">
                                            {error.message}
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                    <div className="col-lg-6">
                        <TextFormInput
                            name="customerAddress"
                            type="text"
                            label={t("address")}
                            placeholder={t("enterYourAddress")}
                            containerClass="col-md-6"
                            control={control}
                            noValidate
                        />
                    </div>
                    <div className="col-lg-12">
                        <TextFormInput
                            name="customerComment"
                            label={t("leaveAComment")}
                            control={control}
                            placeholder={t("leaveAComment")}
                            type="textarea"
                        />
                    </div>
                </div>
            </AccordionBody>
        </AccordionItem>
    );
};

const Room = ({ room, control, roomIdx, getValues }) => {
    return (
        <>
            {[...Array(room?.adults).keys()].map((_, index) => (
                <>
                    <Person
                        index={index + 1}
                        roomIdx={roomIdx}
                        type={"ADT"}
                        control={control}
                        getValues={getValues}
                    />
                </>
            ))}
            {[...Array(room?.children).keys()].map((_, index) => (
                <>
                    <Person
                        index={index + 1}
                        roomIdx={roomIdx}
                        type={"CHD"}
                        control={control}
                        getValues={getValues}
                    />
                </>
            ))}
        </>
    );
};

const Person = ({ index, control, type, roomIdx, getValues }) => {
    const { t } = useTranslation();
    const hotelState = JSON.parse(localStorage.getItem("hotelState") || "{}");
    const rooms = hotelState?.rooms || [];

    const isDuplicatePerson = (firstName, lastName, roomIdx, index) => {
        for (let i = 1; i <= rooms.length; i++) {
            for (let j = 1; j <= rooms[i - 1].adults; j++) {
                if (i === roomIdx && j === index && type === "ADT") {
                    continue;
                }
                if (
                    firstName === getValues(`firstNameADT${i}${j}`) &&
                    lastName === getValues(`lastNameADT${i}${j}`)
                ) {
                    return true;
                }
            }
            for (let j = 1; j <= rooms[i - 1].children; j++) {
                if (i === roomIdx && j === index && type === "CHD") {
                    continue;
                }
                if (
                    firstName === getValues(`firstNameCHD${i}${j}`) &&
                    lastName === getValues(`lastNameCHD${i}${j}`)
                ) {
                    return true;
                }
            }
        }
        return false;
    };

    return (
        <Row className="g-4 mb-3">
            <Col md={2} className="">
                <label className="form-label font-semibold">
                    {type === "ADT" ? t("adult") : t("child")} : {index}
                </label>
            </Col>
            <Col md={3} className="">
                <label className="text-sm text-gray-600">
                    {t("title")} <span className="text-danger">*</span>
                </label>
                <Controller
                    name={`title${type}${roomIdx}${index}`}
                    control={control}
                    rules={{
                        required: t("titleIsRequired"),
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <>
                            <SimpleSelect
                                searchable={false}
                                options={[
                                    { value: t("Mr"), label: t("Mr"), id: 1 },
                                    { value: t("Mrs"), label: t("Mrs"), id: 2 },
                                    { value: t("Ms"), label: t("Ms"), id: 3 },
                                ]}
                                placeholder={t("title")}
                                onChange={(x) => field.onChange(x)}
                                styleName="bordered"
                            />
                            {error && (
                                <div className="text-red-500 text-[12px]">
                                    {error.message}
                                </div>
                            )}
                        </>
                    )}
                />
            </Col>
            <Col md={7}>
                <label className="text-sm text-gray-600">
                    {t("fullName")} <span className="text-danger">*</span>
                </label>
                <div className="d-flex flex-column flex-md-row gap-2">
                    <TextFormInput
                        control={control}
                        name={`firstName${type}${roomIdx}${index}`}
                        placeholder={t("firstName")}
                        rules={{
                            required: t("firstNameIsRequired"),
                            pattern: {
                                value: /^[a-zA-Z\u0621-\u064A\u0660-\u0669\s]*$/,
                                message: t("invalidName"),
                            },
                            validate: (value) => {
                                if (value.length < 3) {
                                    return "firstNameTooShort";
                                }
                                const lastName = getValues(
                                    `lastName${type}${roomIdx}${index}`
                                );
                                if (
                                    isDuplicatePerson(
                                        value,
                                        lastName,
                                        roomIdx,
                                        index
                                    )
                                ) {
                                    return "duplicatePersonError";
                                }
                                return true;
                            },
                        }}
                    />
                    <TextFormInput
                        control={control}
                        name={`lastName${type}${roomIdx}${index}`}
                        placeholder={t("lastName")}
                        rules={{
                            required: t("lastNameIsRequired"),
                            pattern: {
                                value: /^[a-zA-Z\u0621-\u064A\u0660-\u0669\s]*$/,
                                message: t("invalidName"),
                            },
                            validate: (value) => {
                                if (value.length < 3) {
                                    return "lastNameTooShort";
                                }
                                const firstName = getValues(
                                    `firstName${type}${roomIdx}${index}`
                                );
                                if (
                                    isDuplicatePerson(
                                        firstName,
                                        value,
                                        roomIdx,
                                        index
                                    )
                                ) {
                                    return "duplicatePersonError";
                                }
                                return true;
                            },
                        }}
                    />
                </div>
            </Col>
        </Row>
    );
};
